import { useCallback, useEffect, useState } from "react";

// Mirrors VP Trop's src/lib/theme.ts, except the desk starts dark: traders
// watch charts for hours, and light is one click away in the top bar.

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";
export const THEME_EVENT = "vp:theme";

// Matches --background in each theme so the browser chrome blends in.
const THEME_COLOR: Record<Theme, string> = {
	light: "#F3F5F4",
	dark: "#0A0A0B",
};

export function getStoredTheme(): Theme | null {
	try {
		const value = localStorage.getItem(STORAGE_KEY);
		return value === "light" || value === "dark" ? value : null;
	} catch {
		return null;
	}
}

export function getInitialTheme(): Theme {
	return getStoredTheme() ?? "dark";
}

export function currentTheme(): Theme {
	return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}

export function applyTheme(theme: Theme) {
	const root = document.documentElement;
	root.classList.toggle("dark", theme === "dark");
	root.classList.toggle("light", theme === "light");
	root.dataset.theme = theme;

	const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
	if (meta) meta.content = THEME_COLOR[theme];

	// Canvas widgets (the candle chart) can't follow CSS variables on their own.
	window.dispatchEvent(new CustomEvent<Theme>(THEME_EVENT, { detail: theme }));
}

/** Call once before the app renders to avoid a flash of the wrong theme. */
export function applyStoredTheme() {
	applyTheme(getInitialTheme());
}

export function useTheme(): [Theme, () => void] {
	const [theme, setTheme] = useState<Theme>(getInitialTheme);

	useEffect(() => {
		applyTheme(theme);
		try {
			localStorage.setItem(STORAGE_KEY, theme);
		} catch {
			// Storage unavailable (private mode): the theme still applies for this session.
		}
	}, [theme]);

	const toggle = useCallback(() => {
		setTheme((current) => (current === "dark" ? "light" : "dark"));
	}, []);

	return [theme, toggle];
}

/** The active theme, for components that must re-render when it changes. */
export function useActiveTheme(): Theme {
	const [theme, setTheme] = useState<Theme>(currentTheme);
	useEffect(() => {
		const onChange = (e: Event) => setTheme((e as CustomEvent<Theme>).detail);
		window.addEventListener(THEME_EVENT, onChange);
		return () => window.removeEventListener(THEME_EVENT, onChange);
	}, []);
	return theme;
}
